import React, { useState } from "react";
import PropTypes from "prop-types";
import { C } from "../../reporting/reportingStyles";
import { PINNED_FOOTER_KEYS } from "./posReceiptTemplateMeta";

/**
 * A paper mock-up of what the POS prints from a template: the template's header rows, then the
 * fixed Epson lines (call number, date, order ref, order type), the product block, totals and
 * payment lines, the footer rows, the phone line and the QR — in the POS's own order.
 *
 * Widths follow the POS: 80mm = a 400px-wide view, 58mm = 269px; drawn at 0.8 scale to fit the
 * card. The POS does not scale fonts on narrow paper and neither does this.
 */

const PAPER = { 80: 400, 58: 269 };
const SCALE = 0.8;
// Epson dots → the POS's 400px layout: 570 dots across 80mm. A QR is ~25 modules a side.
const QR_PX_PER_SIZE = (400 / 570) * 25;

const SAMPLE = {
  company: {
    brandName: "Wags Coffee",
    arabicName: "واقز كوفي",
    vat: "300012345600003",
    header: ["Riyadh, King Fahd Rd", "Al Olaya"],
    crNumber: "1010123456",
    footer: ["Thank you for your visit", "شكراً لزيارتكم"],
    phone: "0501234567",
  },
  callNo: "2671",
  dateTime: "04/11/2026 10:32",
  orderRef: "POS/00042",
  orderType: "Dine In",
  customer: "Walk in",
  user: "Ahmed",
  items: [
    {
      title: "Spanish Latte", arabic: "سبانيش لاتيه", qty: 2, price: 18,
      modifiers: [{ en: "Oat Milk", ar: "حليب شوفان", qty: 2, price: 3 }],
    },
    { title: "Croissant", arabic: "كرواسون", qty: 1, price: 12, modifiers: [] },
  ],
  totals: { amount: 54, net: 46.96, vat: 7.04, total: 54 },
  payment: { name: "Cash", amount: 54 },
};

const money = (n) => Number(n).toFixed(2);

const PosReceiptPreview = ({ layout, logoUrl, company }) => {
  const [paper, setPaper] = useState(80);
  const width = PAPER[paper] * SCALE;
  const co = { ...SAMPLE.company, ...(company || {}) };
  const items = layout?.items || {};
  const totals = layout?.totals || {};
  const px = (n) => `${(Number(n) || 0) * SCALE}px`;

  const textStyle = (row, extra = {}) => ({
    fontSize: px(row?.fontSize ?? 16),
    fontWeight: row?.fontWeight ?? "normal",
    textAlign: row?.align ?? "center",
    lineHeight: 1.25,
    margin: 0,
    color: "#000",
    ...extra,
  });
  const font = (cfg, size = cfg?.fontSize) => ({ fontSize: px(size ?? 16), fontWeight: cfg?.fontWeight ?? "normal", lineHeight: 1.25, color: "#000" });
  const divider = { borderTop: "1px dashed #000", margin: `${4 * SCALE}px 0` };
  const line = { borderBottom: "1px solid #000" };
  const between = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 };
  const selfAlign = { left: "flex-start", center: "center", right: "flex-end" };

  const customImage = (row) => {
    const w = Math.min(row.width * SCALE, width);
    const h = row.height * (w / (row.width * SCALE)) * SCALE;
    return (
      <div key={row.id} style={{ display: "flex", justifyContent: selfAlign[row.align] || "center", margin: `${3 * SCALE}px 0` }}>
        {row.url ? (
          <img src={row.url} alt="" style={{ width: w, height: h, objectFit: "contain", filter: "grayscale(1) contrast(1.4)" }} />
        ) : (
          <div style={{ width: w, height: h, border: "1px dashed #999", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#999" }}>image</div>
        )}
      </div>
    );
  };

  const textLines = (row) => (
    <div key={row.id}>
      {row.en ? <p style={textStyle(row)}>{row.en}</p> : null}
      {row.ar ? <p style={textStyle(row)}>{row.ar}</p> : null}
      {!row.en && !row.ar ? <p style={textStyle(row, { color: "#999" })}>(empty text line)</p> : null}
    </div>
  );

  const segments = (lines, row) => lines.map((value, i) => {
    const bold = /^text-bold:/i.test(value);
    const text = value.replace(/^text-bold:/i, "").trim();
    return <p key={i} style={textStyle(row, bold ? { fontSize: px(row.boldFontSize ?? 26), fontWeight: "bold" } : {})}>{text}</p>;
  });

  const headerRow = (row) => {
    if (row.visible === false) return null;
    switch (row.key) {
      case "logo":
        return (
          <div key="logo" style={{ display: "flex", justifyContent: "center" }}>
            {logoUrl ? (
              <img src={logoUrl} alt="logo" style={{ width: Math.min(row.width * SCALE, width), height: row.height * SCALE, objectFit: "contain" }} />
            ) : (
              <div style={{ width: Math.min(row.width * SCALE, width), height: row.height * SCALE, border: "1px dashed #999", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#999" }}>
                logo {row.width}×{row.height}
              </div>
            )}
          </div>
        );
      case "brandName": return <p key="brandName" style={textStyle(row)}>{co.brandName}</p>;
      case "arabicName": return <p key="arabicName" style={textStyle(row)}>{co.arabicName}</p>;
      case "invoiceTitle": return <div key="invoiceTitle"><p style={textStyle(row)}>Simplified Tax Invoice</p><p style={textStyle(row)}>فاتورة ضريبية مبسطة</p></div>;
      case "vatNumber": return <p key="vatNumber" style={textStyle(row)}>VAT# {co.vat}</p>;
      case "companyHeader": return <div key="companyHeader">{segments(co.header, row)}</div>;
      case "crNumber": return <p key="crNumber" style={textStyle(row)}>{co.crNumber} : السجل التجاري</p>;
      case "text": return textLines(row);
      case "image": return customImage(row);
      default: return null;
    }
  };

  const footerRow = (row) => {
    if (row.visible === false) return null;
    switch (row.key) {
      case "invoiceDetail": return <div key="invoiceDetail">{segments(co.footer, row)}</div>;
      case "text": return textLines(row);
      case "image": return customImage(row);
      default: return null;
    }
  };

  const productName = (it) => {
    const show = items.arabicName?.show !== false && it.arabic;
    if (!show) return { line: it.title, ar: null };
    return items.arabicName?.placement === "inline" ? { line: `${it.title} (${it.arabic})`, ar: null } : { line: it.title, ar: it.arabic };
  };
  const modifierName = (m) => {
    const show = items.modifiers?.arabic?.show !== false && m.ar;
    if (!show) return { line: m.en, ar: null };
    return items.modifiers?.arabic?.placement === "newline" ? { line: m.en, ar: m.ar } : { line: `${m.en} (${m.ar})`, ar: null };
  };

  const primary = font(items);
  const secondary = font(items, items.secondaryFontSize);
  const arabicFont = { fontSize: px(items.arabicName?.fontSize ?? 12), lineHeight: 1.25, color: "#000" };
  const totalsFont = font(totals);

  const footerRows = (layout?.footer?.rows || []).filter((r) => !PINNED_FOOTER_KEYS.includes(r.key));
  const phoneRow = (layout?.footer?.rows || []).find((r) => r.key === "phone");
  const qrRow = (layout?.footer?.rows || []).find((r) => r.key === "zatcaQr");
  const qrPx = (qrRow?.size ?? 3) * QR_PX_PER_SIZE * SCALE;

  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 12, alignItems: "center" }}>
        {[80, 58].map((mm) => (
          <button
            key={mm}
            type="button"
            onClick={() => setPaper(mm)}
            style={{
              padding: "4px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, cursor: "pointer",
              border: paper === mm ? `1.5px solid ${C.coral}` : `1px solid ${C.border}`,
              background: paper === mm ? C.coralLight : C.white, color: paper === mm ? C.coral : C.textMed,
            }}
          >
            {mm} mm
          </button>
        ))}
        <span style={{ fontSize: 11, color: C.textLight }}>Sample order · Epson</span>
      </div>

      <div
        data-testid="pos-receipt-preview"
        style={{
          width, margin: "0 auto", background: "#fff", color: "#000", padding: `${10 * SCALE}px ${8 * SCALE}px`,
          boxShadow: "0 2px 12px rgba(0,0,0,0.12)", fontFamily: "Helvetica, Arial, sans-serif",
          direction: "ltr", boxSizing: "content-box",
        }}
      >
        {/* header image */}
        <div style={{ display: "flex", flexDirection: "column", gap: px(layout?.header?.rowGap ?? 0) }}>
          {(layout?.header?.rows || []).map(headerRow)}
        </div>

        {/* native Epson lines */}
        {layout?.epson?.feedBeforeCallNumber ? <div style={{ height: px(14) }} /> : null}
        <p style={{ textAlign: "center", fontSize: px(36), fontWeight: "bold", margin: 0, lineHeight: 1.1 }}>{SAMPLE.callNo}</p>
        <p style={{ textAlign: "right", fontSize: px(12), margin: 0 }}>{SAMPLE.dateTime}</p>
        <div style={divider} />
        <p style={{ fontSize: px(12), margin: 0 }}>Order Ref: {SAMPLE.orderRef}</p>
        <p style={{ fontSize: px(12), margin: 0 }}>{SAMPLE.orderType}</p>

        {/* products image */}
        <div style={{ marginTop: px(6) }}>
          <p style={{ ...secondary, textAlign: "right", margin: 0 }}>Customer: {SAMPLE.customer}</p>
          <p style={{ ...secondary, textAlign: "right", margin: 0, ...line }}>User: {SAMPLE.user}</p>
          <div style={{ display: "flex", flexDirection: "column", gap: px(items.rowGap ?? 5), marginTop: px(4) }}>
            {SAMPLE.items.map((it, i) => {
              const name = productName(it);
              return (
                <div key={i} style={items.rowDivider ? line : null}>
                  <div style={between}>
                    <span style={{ ...primary, flex: 1 }}>{name.line}</span>
                    <span style={{ ...primary, width: "12%" }}>{it.qty}</span>
                    <span style={{ ...primary, width: "6%", textAlign: "center" }}>x</span>
                    <span style={{ ...primary, width: "18%" }}>{money(it.price)}</span>
                    <span style={{ ...primary, width: "20%", textAlign: "right" }}>{money(it.qty * it.price)}</span>
                  </div>
                  {name.ar ? <p style={{ ...arabicFont, textAlign: "right", margin: 0 }}>{name.ar}</p> : null}
                  {items.modifiers?.show !== false && it.modifiers.length ? (
                    <>
                      <p style={{ ...secondary, margin: `${3 * SCALE}px 0` }}>Add Ons</p>
                      {it.modifiers.map((m, mi) => {
                        const mn = modifierName(m);
                        return (
                          <div key={mi}>
                            <div style={between}>
                              <span style={{ ...primary, flex: 1 }}>{mn.line}</span>
                              <span style={{ ...primary, width: "12%" }}>{m.qty}</span>
                              <span style={{ ...primary, width: "6%", textAlign: "center" }}>x</span>
                              <span style={{ ...primary, width: "18%" }}>{money(m.price)}</span>
                              <span style={{ ...primary, width: "20%", textAlign: "right" }}>{money(m.qty * m.price)}</span>
                            </div>
                            {mn.ar ? <p style={{ ...arabicFont, textAlign: "right", margin: 0 }}>{mn.ar}</p> : null}
                          </div>
                        );
                      })}
                    </>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>

        <div style={divider} />

        {/* totals image */}
        <div style={{ display: "flex", flexDirection: "column", gap: px(totals.rowGap ?? 0) }}>
          <div style={between}><span style={totalsFont}>Amount المجموع</span><span style={totalsFont}>{money(SAMPLE.totals.amount)}</span></div>
          <div style={between}><span style={totalsFont}>Net Amount المجموع</span><span style={totalsFont}>{money(SAMPLE.totals.net)}</span></div>
          <div style={{ ...between, ...(totals.vatDivider ? line : null) }}><span style={totalsFont}>VAT (15%) ضريبة القيمة المضافة</span><span style={totalsFont}>{money(SAMPLE.totals.vat)}</span></div>
          <div style={{ ...between, borderTop: "1px solid #000" }}><span style={totalsFont}>Total الاجمالي</span><span style={totalsFont}>{money(SAMPLE.totals.total)}</span></div>
          <div style={{ ...between, ...line }}><span style={totalsFont}>{SAMPLE.payment.name}</span><span style={totalsFont}>{money(SAMPLE.payment.amount)}</span></div>
          {footerRows.map(footerRow)}
        </div>

        <div style={divider} />

        {phoneRow?.visible !== false ? <p style={{ textAlign: "center", fontSize: px(12), margin: 0 }}>Phone No # {co.phone}</p> : null}
        <div style={{ display: "flex", justifyContent: "center", marginTop: px(6) }}>
          <div
            data-testid="pos-receipt-preview-qr"
            style={{
              width: qrPx, height: qrPx,
              backgroundImage: "repeating-linear-gradient(0deg, #000 0 3px, #fff 3px 6px), repeating-linear-gradient(90deg, #000 0 3px, #fff 3px 6px)",
              backgroundBlendMode: "difference", border: "2px solid #000",
            }}
          />
        </div>
      </div>
    </div>
  );
};

PosReceiptPreview.propTypes = {
  layout: PropTypes.object.isRequired,
  logoUrl: PropTypes.string,
  company: PropTypes.object,
};

export default PosReceiptPreview;
